import { ReactElement, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import { competitions, regions } from 'utils/constants/sportsbook/data-fields';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { isMobile } from 'utils/is-mobile';
import { getLivePreMatchSportTypes } from 'utils/sportsbook/sport-data';
import RidGenerator from 'utils/swarm/rid-generator';
import { ESportsbookPageTypes } from 'interfaces/sportsbook-configs';
import { Region } from 'interfaces/sportsbook-data-levels';
import { executeCommand, unsubscribeCommand } from 'services/get-data-level';
import { NoItem } from 'components/Shared/NoItem';
import { RegionSkeleton } from 'components/UI/Skeletons/RegionSkeleton';
import { NavigationCollapseWrapper } from 'newcomponents/Shared/sportsbook/NavigationCollapseWrapper';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import { useMounted } from 'hooks/useMounted';
import {
  setRegionCompData,
  setShowNoItemWrapper
} from 'store/actions/new-sportsbook';
import { getRegionCompData } from 'store/selectors/new-sportsbook';
import { getTimeFiltersData } from 'store/selectors/sport-data';

export const SportsNavigation = ({
  sportAlias
}: {
  sportAlias: string;
}): ReactElement => {
  const configs = useSportsbookConfigs();
  const dispatch = useDispatch();
  const { mounted } = useMounted();
  const { t } = useTranslation();

  const timeStamp = useSelector(getTimeFiltersData);
  const regionCompData = useSelector(getRegionCompData)?.[sportAlias];

  useEffect(() => {
    let shouldUnsubscribe = false;

    if (!sportAlias && !SpringConfigs.MOCKED_DATA) {
      return;
    }

    dispatch(
      setShowNoItemWrapper({
        game: false,
        competitions: false,
        regions: false
      })
    );

    shouldUnsubscribe = true;
    const rid = RidGenerator.gForSubscribe(`Prematch_${sportAlias}`);

    const where: Record<string, any> = {
      sport: {
        alias: sportAlias,
        type: {
          '@in': getLivePreMatchSportTypes(configs.sportType)
        }
      },
      game: {
        ...(configs.pageType === ESportsbookPageTypes.live
          ? { type: 1 }
          : {
              '@or': [
                { type: { '@in': [0, 2] } },
                {
                  visible_in_prematch: 1
                }
              ]
            })
      }
    };

    const relations: Record<string, any> = {
      ...regions,
      ...competitions
    };

    const hasTimeStamp = timeStamp && Object.keys(timeStamp).length > 0;

    if (hasTimeStamp) {
      where.game.start_ts = timeStamp;
    }

    if (sportAlias || SpringConfigs.MOCKED_DATA) {
      executeCommand(
        rid,
        relations,
        where,
        (data: { region: Record<string, Region> }) => {
          if (mounted.current && data.region) {
            if (
              isMobile() &&
              hasTimeStamp &&
              !Object.keys(data.region).length
            ) {
              return;
            }

            if (!Object.keys(data.region).length) {
              dispatch(
                setShowNoItemWrapper({
                  game: true,
                  competitions: true,
                  regions: true
                })
              );
            }

            dispatch(setRegionCompData({ [sportAlias]: data.region }));
          }
        },
        true,
        (data: { region: Record<string, Region> }) => {
          if (mounted.current && data.region) {
            if (hasTimeStamp && !Object.keys(data.region).length) {
              return;
            }

            dispatch(setRegionCompData({ [sportAlias]: data.region }));
          }
        }
      );
    }

    return () => {
      if (shouldUnsubscribe) {
        unsubscribeCommand(rid);
      }
    };
  }, [sportAlias, timeStamp]);

  const orderedData = useMemo(() => {
    return regionCompData && sortByOrder(Object.values(regionCompData));
  }, [regionCompData]);

  if (!orderedData) {
    return <RegionSkeleton />;
  }

  if (!Object.keys(orderedData || {}).length) {
    return <NoItem title={t('sportsbook.noCompetition')} />;
  }

  return (
    <NavigationCollapseWrapper
      navigationData={orderedData}
      levelAlias={sportAlias}
      regionLevel
    />
  );
};
